import React from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import {
  Alert,
  Icon,
  Layout,
  Input,
  Table,
  Switch,
  Button,
  Modal,
  Badge,
  message,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { withNamespaces } from "react-i18next";

import classes from './FinanceCustomersEditor.css';

import isAuthenticated from '../../utils/isAuthenticated';
import {
  getFinanceCustomersSearchRequest,
  deleteFinanceCustomerRequest,
  activateFinanceCustomerRequest,
  getSitesRequest
} from '../../api/requests';
import { logout } from "../../reducers/user/actions";
import {
  getFinanceCreatePermission,
  getFinanceDeletePermission,
  getFinanceUpdatePermission,
} from "../../reducers/user/selectors";
import FinanceCustomerForm  from "../../components/FinanceCustomerForm/FinanceCustomerForm";

const { Content } = Layout;
const { Search } = Input;

class FinanceCustomersEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      customers: [],
      sites: [],
      nameSearch: null,
      errorMessage: null,
      responseMessage: null,
      isLoading: false,
      site: null,
      order: 'asc',
      currentPage: 1,
      total: 1,
      pageSize: 25,
      eventVisibleDropdown: false,
      eventsSelected: {},
      sortedInfo: {},
      customerEdit: null,
      approvedSelected: null,
    };
  }

  async componentDidMount() {
    this.onNameSearch();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) { if (e.response.status === 401) { this.props.logout();  return; } console.log(e); }
  }

  onNameSearch = async (value) => {
    this.setState({ nameSearch: value, eventsSelected: {}, currentPage: 1 }, this.getCustomers);
  };

  getCustomers = async (page = this.state.currentPage, per_page = this.state.pageSize) => {
    this.setState({ errorMessage: null, isLoading: true, responseMessage: null });

    try {
      const { data } = await getFinanceCustomersSearchRequest({
        nameSearch: this.state.nameSearch,
        order_direction: this.state.order,
        page,
        per_page,
        order_column: get(this.state, 'sortedInfo.columnKey'),
      });

      if (Array.isArray(data.data)) {
        this.setState({ customers: data.data, total: data.total_pages * per_page, currentPage: page });
      } else {
        this.setState({ customers: data, total: 1 * per_page, currentPage: page });
      }
    } catch (e) {
      if (get(e, 'response.status') === 401) { this.props.logout();  return; }
      this.setState({ customers: null, errorMessage: get(e, 'response.data.error', 'Error') });
    }

    this.setState({ isLoading: false });
  };

  onNameChange = (e) => {
    this.setState({ nameSearch: e.target.value });
  };

  handleTableChange = (pagination, filter, sorter) => {
    this.setState({
      isLoading: true,
      pageSize: pagination.pageSize,
      sortedInfo: sorter,
      order: get(sorter, 'order') === 'ascend' ? 'asc' : get(sorter, 'order') === 'descend' ? 'desc' : 'asc',
    }, () => this.getCustomers(pagination.current, pagination.pageSize));
  };

  onCustomerEdit = item => () => {
    this.setState({ customerEdit: item });
  };

  onCustomerEditHide = () => {
    this.setState({ customerEdit: null });
  };

  onCustomerEditSubmit = () => {
    this.setState({ customerEdit: null }, this.getCustomers);
  };

  addCustomer = () => {
    this.setState({ customerEdit: {} });
  };

  onSwitchCustomerActivation = (id, state) => async () => {
    try {
      if (state === 'active') {
        await deleteFinanceCustomerRequest(id);
      } else {
        await activateFinanceCustomerRequest(id);
      }

      message.success(this.props.i18n.t('Saved'));

      this.getCustomers();
    } catch (e) {
      if (get(e, 'response.status') === 401) { this.props.logout();  return; }
      message.error('Error');
      console.log(e);
    }
  };

  clearFilters = () => { this.setState({ nameSearch: null, currentPage: 1 }, this.getCustomers); };

  render() {
    const { errorMessage, isLoading, responseMessage, sites, customers, currentPage, total, pageSize, customerEdit, sortedInfo, nameSearch } = this.state;
    const { financeCreatePermission } = this.props;

    const columns = [
      {
        dataIndex: "name",
        key: "name",
        title: this.props.i18n.t("Customer Name"),
        render: (val, record) => (<div className={classes.nameWrapper}>
          <div style={{ width: '86px' }}>
            <Badge count={record.state} style={{ backgroundColor: record.state === 'active' ? '#52c41a' : '#777' }} />
            <br/>
          </div>
          {val}
        </div>),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'name' && sortedInfo.order,
      },
      {
        dataIndex: "site_id",
        key: "site_id",
        title: this.props.i18n.t("Site"),
        render: (val) => get(this.state.sites.find(({ id }) => id === val), 'name'),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'site_id' && sortedInfo.order,
      },
      {
        dataIndex: "currency",
        key: "currency",
        title: this.props.i18n.t("Currency"),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'currency' && sortedInfo.order,
      },
      {
        dataIndex: "email",
        key: "email",
        title: this.props.i18n.t("Email"),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'email' && sortedInfo.order,
      },
      {
        dataIndex: "created",
        key: "created",
        title: this.props.i18n.t("Created"),
        render: (val) => moment(val).format('YYYY-MM-DD HH:mm'),
        sorter: true,
        sortOrder: get(sortedInfo, 'columnKey') === 'created' && sortedInfo.order,
      },
      {
        dataIndex: "id",
        key: "id",
        title: this.props.i18n.t("Actions"),
        render: (val, item) => (<div className={classes.actions}>
          <Tooltip placement="topLeft" title={this.props.i18n.t("Edit Customer")}>
            <Icon type="edit" theme="filled" className={classes.iconEdit} onClick={this.onCustomerEdit(item)} />
          </Tooltip>
          &nbsp; &nbsp;
          <div style={{ width: '90px', display: 'flex' }}>
            {((this.props.financeUpdatePermission && item.state === 'inactive') || (this.props.financeDeletePermission && item.state === 'active')) && <div>
              <b className={classes.switchLabel}>{this.props.i18n.t("State")}</b><br/>
              <Tooltip placement="topLeft" title={this.props.i18n.t("Activate / Deactivate Customer")}>
                <Switch className={classes.reactivateBtn} checked={item.state === 'active'} onChange={this.onSwitchCustomerActivation(val, item.state)} />
              </Tooltip>
            </div>}
          </div>
        </div>)
      },
    ];

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <Content className={classes.content}>
            <h3 className="headline-default">{this.props.i18n.t("Customers")}</h3>

            <div className={classes.searchWrapper}>
              <label className={classes.searchWrapperSearch}>
                <b className={classes.searchWrapperLabel}>{this.props.i18n.t("SEARCH")}</b>
                <Search
                  placeholder={this.props.i18n.t("By name")}
                  enterButton={this.props.i18n.t("Search")}
                  size="large"
                  onSearch={this.onNameSearch}
                  value={nameSearch}
                  onChange={this.onNameChange}
                  style={{ maxWidth: '500px'}}
                />
              </label>

              <span className={classes.clearButton} onClick={this.clearFilters}>{this.props.i18n.t("Clear")}</span>
            </div>

            {errorMessage && (
              <Alert
                type="error"
                message={errorMessage}
                className={classes.alert}
              />
            )}

            {responseMessage && (
              <Alert
                type="success"
                message={responseMessage}
                className={classes.alert}
              />
            )}

            {financeCreatePermission && (
              <div>
                <Button type="primary" onClick={this.addCustomer}>
                  {this.props.i18n.t("Add new customer")}
                </Button>
                <br />
                <br />
              </div>
            )}

            <Table
              columns={columns}
              dataSource={customers}
              loading={isLoading}
              rowKey="id"
              onChange={this.handleTableChange}
              pagination={{
                onShowSizeChange: (current, size) => {
                  this.setState({ pageSize: size });
                },
                current: currentPage,
                total,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                position: 'both',
              }}
            />

            {/*{isLoading && <Icon type="loading" />}*/}

            <Modal
              visible={!!customerEdit}
              onCancel={this.onCustomerEditHide}
              footer={null}
            >
              {customerEdit && (
                <FinanceCustomerForm
                  {...customerEdit}
                  isNew={customerEdit && !customerEdit.id}
                  onSubmit={this.onCustomerEditSubmit}
                  onCancel={this.onCustomerEditHide}
                  sites={sites}
                />
              )}
            </Modal>
          </Content>
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  financeCreatePermission: getFinanceCreatePermission(state),
  financeDeletePermission: getFinanceDeletePermission(state),
  financeUpdatePermission: getFinanceUpdatePermission(state),
});
const mapDispatchToProps = { logout };

export default isAuthenticated(connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(FinanceCustomersEditor)));



// WEBPACK FOOTER //
// ./src/containers/FinanceCustomersEditor/FinanceCustomersEditor.js